import { useState} from 'react';
import { motion } from 'framer-motion';
import { Upload, X, AlertCircle, Image, Mail, Camera } from 'lucide-react';
import toast from 'react-hot-toast';

import { Card, CardContent, CardHeader, CardTitle } from '../../ui/Card';
import { Input } from '../../ui/Input';
import { Button } from '../../ui/Button';

import projectService from '../../../services/projectService';

const MediaContactStep = ({ formData, updateFormData, errors, setErrors }) => {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});

  const handleInputChange = (field, value) => {
    updateFormData({ [field]: value });
  };

  const validateField = (field, value) => {
    const newErrors = { ...errors };

    switch (field) {
      case 'contact_email':
        if (!value) {
          newErrors.contact_email = 'Contact email is required';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
          newErrors.contact_email = 'Please enter a valid email address';
        } else {
          delete newErrors.contact_email;
        }
        break;

      default:
        break;
    }

    setErrors(newErrors);
  };

  const validateImageFile = (file) => {
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/jpg'];
    const maxSize = 5 * 1024 * 1024; // 5MB
    
    if (!allowedTypes.includes(file.type)) {
      return { valid: false, error: 'Invalid file type. Only JPEG, PNG, and WebP allowed.' };
    }
    
    if (file.size > maxSize) {
      return { valid: false, error: 'File size exceeds 5MB limit.' };
    }
    
    return { valid: true };
  };

  const validateVideoFile = (file) => {
    const allowedTypes = ['video/mp4', 'video/webm', 'video/quicktime'];
    const maxSize = 40 * 1024 * 1024; // 40MB
    
    if (!allowedTypes.includes(file.type)) {
      return { valid: false, error: 'Invalid file type. Only MP4, WebM, and MOV allowed.' };
    }
    
    if (file.size > maxSize) {
      return { valid: false, error: 'Video file size exceeds 40MB limit.' };
    }
    
    return { valid: true };
  };

  const handleImageUpload = async (file, type = 'gallery') => {
    const validation = validateImageFile(file);
    if (!validation.valid) {
      toast.error(validation.error);
      return;
    }

    try {
      setIsUploading(true);
      setUploadProgress(prev => ({ ...prev, [type]: 0 }));

      const response = await projectService.uploadImage(file);

      if (response.success) {
        if (type === 'cover') {
          updateFormData({ cover_image: response.data.url });
          const newErrors = { ...errors };
          delete newErrors.cover_image;
          setErrors(newErrors);
        } else {
          const currentGallery = formData.gallery_images || [];
          if (currentGallery.length < 3) {
            const newGallery = [...currentGallery, response.data.url];
            updateFormData({ gallery_images: newGallery });
          } else {
            toast.error('Maximum 3 gallery images allowed');
            return;
          }
        }
        toast.success('Image uploaded successfully!');
      } else {
        toast.error('Upload failed: ' + (response.error?.message || 'Unknown error'));
      }
    } catch (error) {
      toast.error('Failed to upload image: ' + error.message);
    } finally {
      setIsUploading(false);
      setUploadProgress(prev => {
        const next = { ...prev };
        delete next[type];
        return next;
      });
    }
  };

  const handleVideoUpload = async (file) => {
    const validation = validateVideoFile(file);
    if (!validation.valid) {
      toast.error(validation.error);
      return;
    }

    try {
      setIsUploading(true);
      setUploadProgress(prev => ({ ...prev, video: 0 }));

      const response = await projectService.uploadVideo(file, (progressEvent) => {
        const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        setUploadProgress(prev => ({ ...prev, video: progress }));
      });

      if (response.success) {
        updateFormData({ video_url: response.data.url });
        toast.success('Video uploaded successfully!');
      } else {
        toast.error('Video upload failed: ' + (response.error?.message || 'Unknown error'));
      }
    } catch (error) {
      toast.error('Failed to upload video: ' + error.message);
    } finally {
      setIsUploading(false);
      setUploadProgress(prev => {
        const next = { ...prev };
        delete next.video;
        return next;
      });
    }
  };

  const handleCoverImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) handleImageUpload(file, 'cover');
  };

  const handleGalleryImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const currentGallery = formData.gallery_images || [];
    if (currentGallery.length >= 3) {
      toast.error('Maximum 3 gallery images allowed');
      return;
    }
    handleImageUpload(file, `gallery_${currentGallery.length}`);
  };

  const handleVideoFileChange = (e) => {
    const file = e.target.files[0];
    if (file) handleVideoUpload(file);
  };

  const removeGalleryImage = async (index) => {
    const currentGallery = formData.gallery_images || [];
    const imageUrl = currentGallery[index];
    if (!imageUrl) {
      updateFormData({ gallery_images: currentGallery.filter((_, i) => i !== index) });
      return;
    }
    try {
      try { await projectService.deleteImage(imageUrl); } catch (e) { console.warn(e); }
      updateFormData({ gallery_images: currentGallery.filter((_, i) => i !== index) });
      toast.success('Image removed successfully');
    } catch (error) {
      toast.error('Failed to remove image: ' + error.message);
    }
  };

  const removeCoverImage = async () => {
    try {
      if (formData.cover_image) {
        try { await projectService.deleteImage(formData.cover_image); } catch (e) { console.warn(e); }
      }
      updateFormData({ cover_image: null });
      toast.success('Cover image removed successfully');
    } catch (error) {
      toast.error('Failed to remove cover image: ' + error.message);
    }
  };

  const removeVideo = () => {
    updateFormData({ video_url: null });
    toast.success('Video removed successfully');
  };

  return (
    <div className="space-y-6 max-w-full overflow-hidden px-1 sm:px-0">
      {/* Header */}
      <Card className="border-0 shadow-soft">
        <CardHeader className="p-4 sm:p-6">
          <CardTitle className="text-xl sm:text-h4 font-heading text-neutral-900 flex items-center gap-2">
            📷 Media & Contact
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4 sm:p-6">
          <p className="text-sm sm:text-p2-r text-neutral-600">
            Showcase your project with high-quality media and provide contact details.
          </p>
        </CardContent>
      </Card>

      {/* Cover Image */}
      <Card className="border-0 shadow-soft">
        <CardHeader className="p-4 sm:p-6">
          <CardTitle className="text-base sm:text-p1-m text-neutral-900 flex items-center gap-2">
            🖼️ Cover Image *
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4 sm:p-6 space-y-4">
          {!formData.cover_image ? (
            <div className="border-2 border-dashed border-neutral-300 hover:border-primary-teal rounded-xl p-6 sm:p-8 text-center bg-neutral-50/50 transition-all duration-300">
              <Camera className="w-10 h-10 sm:w-12 sm:h-12 text-neutral-400 mx-auto mb-3" />
              <p className="text-base font-medium text-neutral-900 mb-1">Upload Cover</p>
              <p className="text-xs text-neutral-500 mb-4">JPEG, PNG, WebP (max 5MB)</p>
              <input
                type="file"
                accept="image/*"
                onChange={handleCoverImageUpload}
                className="hidden"
                id="cover-upload"
                disabled={isUploading}
              />
              <Button variant="outline" asChild disabled={isUploading} className="w-full sm:w-auto">
                <label htmlFor="cover-upload" className="cursor-pointer">
                  <Upload className="w-4 h-4 mr-2" />
                  {isUploading ? 'Uploading...' : 'Choose Image'}
                </label>
              </Button>
            </div>
          ) : (
            <div className="relative group rounded-xl overflow-hidden shadow-md">
              <img src={formData.cover_image} alt="Cover" className="w-full h-48 sm:h-64 object-cover" />
              <Button
                variant="destructive"
                size="icon"
                onClick={removeCoverImage}
                className="absolute top-2 right-2 rounded-full h-8 w-8 active:scale-95 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity shadow-lg"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          )}
          {errors.cover_image && (
            <div className="flex items-center gap-1.5 text-red-600 text-sm mt-1">
              <AlertCircle className="w-4 h-4" />
              <span>{errors.cover_image}</span>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Video Section */}
      <Card className="border-0 shadow-soft">
        <CardHeader className="p-4 sm:p-6">
          <CardTitle className="text-base sm:text-p1-m text-neutral-900 flex items-center gap-2">
            🎥 Project Video
            <span className="text-xs sm:text-p2-r text-neutral-500 font-normal">(Optional)</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4 sm:p-6 space-y-4">
          <p className="text-sm text-neutral-600">
            A short video can significantly increase your project's success.
          </p>
          {!formData.video_url ? (
            <div className="border-2 border-dashed border-neutral-300 hover:border-primary-teal rounded-xl p-6 sm:p-8 text-center bg-neutral-50/50">
              <Upload className="w-10 h-10 sm:w-12 sm:h-12 text-neutral-400 mx-auto mb-3" />
              <p className="text-base font-medium text-neutral-900 mb-1">Upload Video</p>
              <p className="text-xs text-neutral-500 mb-4">MP4, WebM (max 40MB)</p>
              {uploadProgress.video !== undefined ? (
                <div className="w-full max-w-xs mx-auto space-y-2">
                  <div className="w-full bg-neutral-200 rounded-full h-2">
                    <div 
                      className="bg-primary-teal h-2 rounded-full transition-all duration-300"
                      style={{ width: `${uploadProgress.video}%` }}
                    />
                  </div>
                  <p className="text-xs text-primary-teal font-medium">{uploadProgress.video}% Uploaded</p>
                </div>
              ) : (
                <>
                  <input
                    type="file"
                    accept="video/mp4,video/webm,video/quicktime"
                    onChange={handleVideoFileChange}
                    className="hidden"
                    id="video-upload"
                    disabled={isUploading}
                  />
                  <Button variant="outline" asChild disabled={isUploading} className="w-full sm:w-auto">
                    <label htmlFor="video-upload" className="cursor-pointer">
                      <Upload className="w-4 h-4 mr-2" />
                      {isUploading ? 'Processing...' : 'Choose Video'}
                    </label>
                  </Button>
                </>
              )}
            </div>
          ) : (
            <div className="relative rounded-xl overflow-hidden bg-black aspect-video shadow-md">
              <video src={formData.video_url} controls className="w-full h-full" />
              <Button
                variant="destructive"
                size="icon"
                onClick={removeVideo}
                className="absolute top-2 right-2 rounded-full h-8 w-8 active:scale-95 shadow-lg z-10"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Gallery Images */}
      <Card className="border-0 shadow-soft">
        <CardHeader className="p-4 sm:p-6">
          <CardTitle className="text-base sm:text-p1-m text-neutral-900 flex items-center gap-2">
            🖼️ Gallery Images
            <span className="text-xs sm:text-p2-r text-neutral-500 font-normal">(Optional - up to 3)</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4 sm:p-6 space-y-4">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-4">
            {(formData.gallery_images || []).map((imageUrl, index) => (
              <motion.div key={index} initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="relative group aspect-square rounded-lg overflow-hidden border border-neutral-100 shadow-sm">
                <img src={imageUrl} alt={`Gallery ${index + 1}`} className="w-full h-full object-cover" />
                <Button
                  variant="destructive"
                  size="icon"
                  onClick={() => removeGalleryImage(index)}
                  className="absolute top-1.5 right-1.5 rounded-full h-7 w-7 active:scale-95 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity"
                >
                  <X className="w-3.5 h-3.5" />
                </Button>
              </motion.div>
            ))}
            {(formData.gallery_images || []).length < 3 && (
              <div className="aspect-square border-2 border-dashed border-neutral-300 hover:border-primary-teal rounded-lg flex flex-col items-center justify-center text-center p-2 bg-neutral-50/30 transition-all duration-300">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleGalleryImageUpload}
                  className="hidden"
                  id={`gallery-upload-${(formData.gallery_images || []).length}`}
                  disabled={isUploading}
                />
                <label htmlFor={`gallery-upload-${(formData.gallery_images || []).length}`} className="cursor-pointer w-full h-full flex flex-col items-center justify-center">
                  <Image className="w-6 h-6 sm:w-8 sm:h-8 text-neutral-400 mb-1" />
                  <span className="text-xs sm:text-p2-r text-neutral-600">Add</span>
                </label>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Contact Information */}
      <Card className="border-0 shadow-soft">
        <CardHeader className="p-4 sm:p-6">
          <CardTitle className="text-base sm:text-p1-m text-neutral-900 flex items-center gap-2">
            📧 Contact Email *
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4 sm:p-6 space-y-4">
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
            <Input
              type="email"
              value={formData.contact_email || ''}
              onChange={(e) => handleInputChange('contact_email', e.target.value)}
              onBlur={(e) => validateField('contact_email', e.target.value)}
              placeholder="contact@example.com"
              className={`pl-10 h-11 sm:h-12 ${errors.contact_email ? 'border-red-500 bg-red-50/30' : ''}`}
            />
          </div>
          {errors.contact_email && (
            <div className="flex items-center gap-1.5 text-red-600 text-xs sm:text-sm">
              <AlertCircle className="w-4 h-4" />
              <span>{errors.contact_email}</span>
            </div>
          )}
          <p className="text-xs sm:text-p2-r text-neutral-500">Used for official communication regarding your project.</p>
        </CardContent>
      </Card>

      {/* Guides */}
      <div className="space-y-3 px-1">
        <div className="p-4 bg-teal-50/50 border border-teal-100 rounded-xl space-y-2">
          <div className="flex items-center gap-2 text-teal-800 font-semibold text-sm">
            <Camera className="w-4 h-4" /> Media Guidelines
          </div>
          <ul className="text-xs text-teal-700 space-y-1">
            <li>• Use landscape (16:9) images for best display</li>
            <li>• Max 40MB for video; MP4 recommended</li>
            <li>• Clear, high-res photos attract 50% more donors</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default MediaContactStep;
